/** Application initialization. */

import { initNavigation } from './components/navigation/NavBar';
import { initSocketIO, setViewRefreshHandlers } from './utils/socket';
import { showToast } from './utils/helpers';
import { normalizeTicker } from './utils/formatters';
import { hideAllViews, setActiveNavLink } from './utils/viewNavigation';
import { focusViewHeading } from './utils/a11y';
import {
  useFilterStore,
  type MomentumAlertType,
  type MomentumLevelThreshold,
} from './stores/filter';

let activeNavigationRequestId = 0;

function showMainLoader(): void {
  const mainLoader = document.getElementById('main-loader');
  if (!mainLoader) {
    return;
  }
  mainLoader.classList.remove('hidden');
  mainLoader.style.display = '';
  mainLoader.setAttribute('aria-busy', 'true');
}

function hideMainLoader(): void {
  const mainLoader = document.getElementById('main-loader');
  if (!mainLoader) {
    return;
  }
  mainLoader.classList.add('hidden');
  mainLoader.style.display = 'none';
  mainLoader.setAttribute('aria-busy', 'false');
}

export function initApp(): void {
  // Import renderDashboard function
  async function renderDashboard(handleTickerClick: (ticker: string) => void): Promise<void> {
    const { renderDashboard: renderDashboardView } = await import('./components/views/DashboardView');
    await renderDashboardView(handleTickerClick);
  }
  
  // Set up navigation handlers
  const handlers = {
    handleTickerClick: (ticker: string) => {
      // Navigate to deep dive view
      const navEvent = new CustomEvent('navigate', { detail: { view: 'deep_dive', ticker } });
      window.dispatchEvent(navEvent);
    },
    handleInfluencerClick: (authorName: string) => {
      // TODO: Navigate to influencer deep dive
      if (import.meta.env.DEV) {
        console.log('Navigate to influencer:', authorName);
      }
    },
    refreshDashboard: async () => {
      await renderDashboard(handlers.handleTickerClick);
    },
  };
  
  // Set up view refresh handlers for Socket.IO updates
  setViewRefreshHandlers({
    refreshDashboard: async () => {
      await renderDashboard(handlers.handleTickerClick);
    },
    refreshLiveFeed: async () => {
      const { renderLiveFeed } = await import('./components/views/LiveFeedView');
      await renderLiveFeed({ preserveScrollAndSlice: true });
    },
    refreshInfluencers: async () => {
      const { refreshInfluencersView } = await import('./components/views/InfluencersView');
      await refreshInfluencersView();
    },
    refreshDueDiligence: async () => {
      const { refreshDueDiligenceView } = await import('./components/views/DueDiligenceView');
      await refreshDueDiligenceView();
    },
    refreshNews: async () => {
      const { refreshNewsData } = await import('./components/views/NewsView');
      await refreshNewsData();
    },
    refreshWatchlist: async () => {
      const { renderWatchlist } = await import('./components/views/WatchlistView');
      await renderWatchlist();
    },
    refreshDeepDive: async (ticker: string) => {
      const { refreshDeepDiveData } = await import('./components/views/DeepDiveView');
      await refreshDeepDiveData(ticker);
    },
    refreshInfluencerDeepDive: async (author: string) => {
      const { renderInfluencerDeepDive } = await import('./components/views/InfluencerDeepDiveView');
      const { isInfluencersWindow } = await import('./api/services/influencers');
      const { useNavigationStore } = await import('./stores/navigation');
      const w = useNavigationStore.getState().viewParams?.influencerWindow;
      const windowOpt = w && isInfluencersWindow(w) ? w : undefined;
      await renderInfluencerDeepDive(author, { window: windowOpt });
    },
  });
  
  // Set handlers for navigation
  import('./components/navigation/NavBar').then(module => {
    if (module.setNavigationHandlers) {
      module.setNavigationHandlers(handlers);
    }
  });
  
  // Initialize navigation
  initNavigation();
  
  // Initialize Socket.IO
  initSocketIO();
  
  // Set up global ticker click handler (delegation)
  // This ensures all tickers anywhere on the page are clickable
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    
    // Skip if clicking on buttons or interactive elements
    if (target.classList.contains('remove-watchlist-btn') || 
        target.closest('.remove-watchlist-btn') ||
        target.classList.contains('expand-row-btn') ||
        target.closest('.expand-row-btn') ||
        target.tagName === 'BUTTON' ||
        target.tagName === 'A') {
      return;
    }
    
    const tickerElement = target.closest('[data-ticker]');
    
    if (tickerElement) {
      const tickerRaw = tickerElement.getAttribute('data-ticker');
      if (tickerRaw) {
        e.preventDefault();
        e.stopPropagation();
        // Normalize ticker to ensure consistent database queries
        const ticker = normalizeTicker(tickerRaw);
        if (ticker) {
          // Navigate to deep dive with normalized ticker
          const navEvent = new CustomEvent('navigate', { detail: { view: 'deep_dive', ticker } });
          window.dispatchEvent(navEvent);
        }
      }
    }
  });
  
  // Set up global navigation event listener
  window.addEventListener('navigate', (event: Event) => {
    const customEvent = event as CustomEvent<{
      view: string;
      ticker?: string;
      author?: string;
      influencerWindow?: string;
    }>;
    const { view, ticker, author, influencerWindow } = customEvent.detail;
    const requestId = ++activeNavigationRequestId;
    const isCurrentRequest = () => requestId === activeNavigationRequestId;
    
    // Show/hide header and navigation based on view using CSS class
    if (view === 'landing') {
      // Hide header and navigation for landing page
      document.body.classList.add('landing-page');
    } else {
      // Show header and navigation for all other views
      document.body.classList.remove('landing-page');
    }
    
    // Hide all views first
    hideAllViews();
    const inlineLoadingViews = new Set(['top_influencers', 'due_diligence', 'influencer_deep_dive', 'news']);
    if (inlineLoadingViews.has(view)) {
      hideMainLoader();
    } else {
      showMainLoader();
    }
    
    // Update navigation store (with params for deep dive views)
    import('./stores/navigation').then((module) => {
      const params: { ticker?: string; author?: string; influencerWindow?: string } = {};
      if (ticker) params.ticker = ticker;
      if (author) params.author = author;
      if (influencerWindow) params.influencerWindow = influencerWindow;
      const paramsOut = Object.keys(params).length ? params : undefined;
      module.useNavigationStore.getState().setCurrentView(view, paramsOut);
    });
    
    // Update active nav link
    setActiveNavLink(view);
    
    // Load appropriate view with lazy loading
    switch (view) {
      case 'landing':
        import('./components/views/LandingView').then(module => {
          if (!isCurrentRequest()) return;
          module.renderLanding();
          if (!isCurrentRequest()) return;
          hideMainLoader();
          focusViewHeading('landing');
        });
        break;
      case 'dashboard':
        import('./components/views/DashboardView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderDashboard(handlers.handleTickerClick)).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('dashboard');
          });
        });
        break;
      case 'live_feed':
        import('./components/views/LiveFeedView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderLiveFeed()).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('live_feed');
          });
        });
        break;
      case 'deep_dive':
        if (ticker) {
          import('./components/views/DeepDiveView').then(module => {
            if (!isCurrentRequest()) return;
            Promise.resolve(module.renderDeepDive(ticker)).finally(() => {
              if (!isCurrentRequest()) return;
              hideMainLoader();
              focusViewHeading('deep_dive');
            });
          }).catch(() => {
            if (!isCurrentRequest()) return;
            console.error('Deep dive view not available');
            showToast('Deep dive view not available', true);
            hideMainLoader();
          });
        } else {
          showToast('No ticker specified for deep dive', true);
          hideMainLoader();
        }
        break;
      case 'top_influencers':
        import('./components/views/InfluencersView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderInfluencers()).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('top_influencers');
          });
        });
        break;
      case 'due_diligence':
        import('./components/views/DueDiligenceView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderDueDiligence()).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('due_diligence');
          });
        });
        break;
      case 'news':
        import('./components/views/NewsView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderNews()).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('news');
          });
        });
        break;
      case 'watchlist':
        import('./components/views/WatchlistView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderWatchlist()).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('watchlist');
          });
        });
        break;
      case 'documentation':
        import('./components/views/DocumentationView').then(module => {
          if (!isCurrentRequest()) return;
          Promise.resolve(module.renderDocumentation()).finally(() => {
            if (!isCurrentRequest()) return;
            hideMainLoader();
            focusViewHeading('documentation');
          });
        });
        break;
      case 'influencer_deep_dive':
        if (author) {
          void (async () => {
            try {
              if (!isCurrentRequest()) return;
              const { isInfluencersWindow } = await import('./api/services/influencers');
              if (!isCurrentRequest()) return;
              const { renderInfluencerDeepDive } = await import('./components/views/InfluencerDeepDiveView');
              if (!isCurrentRequest()) return;
              const w =
                influencerWindow && isInfluencersWindow(influencerWindow) ? influencerWindow : undefined;
              await renderInfluencerDeepDive(author, { window: w });
            } catch {
              if (!isCurrentRequest()) return;
              console.error('Influencer deep dive view not available');
              showToast('Influencer deep dive view not available', true);
            } finally {
              if (!isCurrentRequest()) return;
              hideMainLoader();
              focusViewHeading('influencer_deep_dive');
            }
          })();
        } else {
          showToast('No author specified for influencer deep dive', true);
          hideMainLoader();
        }
        break;
      default:
        hideMainLoader();
        break;
    }
  });
  
  // Initialize tooltips
  initTooltips();
  
  // Set up landing page link handler
  const landingPageLink = document.getElementById('landing-page-link');
  if (landingPageLink) {
    landingPageLink.addEventListener('click', (e) => {
      e.preventDefault();
      const navEvent = new CustomEvent('navigate', { detail: { view: 'landing' } });
      window.dispatchEvent(navEvent);
    });
  }

  initFilterSettingsModal(async () => {
    const { currentView } = await import('./stores/navigation').then((module) =>
      module.useNavigationStore.getState()
    );
    if (currentView === 'dashboard') {
      const { renderDashboard } = await import('./components/views/DashboardView');
      await renderDashboard(handlers.handleTickerClick);
    }
  });
  
  // Load initial view (check terms acceptance first, then restore from localStorage or default to dashboard)
  import('./components/views/LandingView').then(landingModule => {
    // Check if user has accepted terms
    if (!landingModule.hasAcceptedTerms()) {
      // Show landing page if terms not accepted
      landingModule.renderLanding();
      return;
    }
    
    // Terms accepted, proceed with normal navigation
    import('./stores/navigation').then(module => {
      const savedView = module.useNavigationStore.getState().getSavedView();
      
      if (savedView) {
        // Restore saved view
        const { view, params } = savedView;
        const navEvent = new CustomEvent('navigate', { 
          detail: { 
            view, 
            ticker: params?.ticker, 
            author: params?.author 
          } 
        });
        window.dispatchEvent(navEvent);
      } else {
        // Default to dashboard
        showMainLoader();
        import('./components/views/DashboardView').then(module => {
          Promise.resolve(module.renderDashboard(handlers.handleTickerClick)).finally(() => {
            hideMainLoader();
            focusViewHeading('dashboard');
          });
        });
      }
    });
  });
  
  if (import.meta.env.DEV) {
    console.log('✅ TKRScan initialized');
  }
}

function initTooltips(): void {
  import('./utils/tooltips').then(module => {
    module.initTooltips();
  }).catch(error => {
    console.error('Failed to initialize tooltips:', error);
  });
}

function initFilterSettingsModal(onFiltersChanged: () => Promise<void>): void {
  const modal = document.getElementById('filters-settings-modal');
  const openBtn = document.getElementById('filters-settings-btn');
  const closeBtn = document.getElementById('filters-settings-close-btn');
  const momentumLevelSelect = document.getElementById(
    'filters-settings-momentum-level'
  ) as HTMLSelectElement | null;
  const momentumTypesContainer = document.getElementById('filters-settings-momentum-types');
  const momentumTypesResetBtn = document.getElementById('filters-settings-momentum-types-reset-btn');
  const hypeMinPriceInput = document.getElementById(
    'filters-settings-hype-min-price'
  ) as HTMLInputElement | null;
  const hypeMaxPriceInput = document.getElementById(
    'filters-settings-hype-max-price'
  ) as HTMLInputElement | null;
  const hypePriceClearBtn = document.getElementById('filters-settings-hype-price-clear-btn');
  const crossSignalMinHypeInput = document.getElementById(
    'filters-settings-cross-signal-min-hype'
  ) as HTMLInputElement | null;

  const velocityInput = document.getElementById('filters-settings-velocity-input') as HTMLInputElement | null;
  const velocityAddBtn = document.getElementById('filters-settings-velocity-add-btn');
  const velocityResetBtn = document.getElementById('filters-settings-velocity-reset-btn');
  const velocityClearBtn = document.getElementById('filters-settings-velocity-clear-btn');
  const velocityList = document.getElementById('filters-settings-velocity-list');

  const momentumInput = document.getElementById('filters-settings-momentum-input') as HTMLInputElement | null;
  const momentumAddBtn = document.getElementById('filters-settings-momentum-add-btn');
  const momentumResetBtn = document.getElementById('filters-settings-momentum-reset-btn');
  const momentumClearBtn = document.getElementById('filters-settings-momentum-clear-btn');
  const momentumList = document.getElementById('filters-settings-momentum-list');

  if (
    !modal ||
    !openBtn ||
    !closeBtn ||
    !momentumLevelSelect ||
    !momentumTypesContainer ||
    !momentumTypesResetBtn ||
    !hypeMinPriceInput ||
    !hypeMaxPriceInput ||
    !hypePriceClearBtn ||
    !crossSignalMinHypeInput ||
    !velocityInput ||
    !velocityAddBtn ||
    !velocityResetBtn ||
    !velocityClearBtn ||
    !velocityList ||
    !momentumInput ||
    !momentumAddBtn ||
    !momentumResetBtn ||
    !momentumClearBtn ||
    !momentumList
  ) {
    return;
  }

  const renderTickerList = (
    listEl: HTMLElement,
    tickers: string[],
    defaults: Set<string>,
    scope: 'velocity' | 'momentum'
  ) => {
    if (tickers.length === 0) {
      listEl.innerHTML = '<p class="text-secondary text-sm">No banned tickers configured.</p>';
      return;
    }
    listEl.innerHTML = tickers
      .map((ticker) => {
        const fromDefault = defaults.has(ticker.toUpperCase());
        return `
          <div class="flex items-center justify-between bg-gray-800/70 border border-gray-700 rounded px-3 py-2">
            <div class="flex items-center gap-2">
              <span class="font-bold text-neon-cyan">$${ticker}</span>
              ${fromDefault ? '<span class="text-xs text-gray-400">(default)</span>' : '<span class="text-xs text-gray-400">(custom)</span>'}
            </div>
            <button type="button" class="filters-settings-remove-btn px-2 py-1 text-xs bg-red-600 hover:bg-red-700 rounded text-white" data-scope="${scope}" data-ticker="${ticker}">Remove</button>
          </div>
        `;
      })
      .join('');
  };

  const renderMomentumTypeCheckboxes = () => {
    const { momentumEnabledTypes } = useFilterStore.getState();
    const enabled = new Set(momentumEnabledTypes);
    momentumTypesContainer
      .querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-type]')
      .forEach((checkbox) => {
        checkbox.checked = enabled.has(checkbox.dataset.type as MomentumAlertType);
      });
  };

  const renderModal = () => {
    const {
      velocityBannedTickers,
      momentumBannedTickers,
      serverDefaultVelocityBanTickers,
      momentumMinLevel,
      hypeMinPrice,
      hypeMaxPrice,
      crossSignalMinHypeScore,
    } = useFilterStore.getState();
    const defaults = new Set(serverDefaultVelocityBanTickers.map((t) => t.toUpperCase()));
    momentumLevelSelect.value = momentumMinLevel;
    hypeMinPriceInput.value = hypeMinPrice == null ? '' : String(hypeMinPrice);
    hypeMaxPriceInput.value = hypeMaxPrice == null ? '' : String(hypeMaxPrice);
    crossSignalMinHypeInput.value = String(crossSignalMinHypeScore);
    renderMomentumTypeCheckboxes();
    renderTickerList(velocityList, velocityBannedTickers, defaults, 'velocity');
    renderTickerList(momentumList, momentumBannedTickers, defaults, 'momentum');
  };

  const openModal = () => {
    renderModal();
    modal.classList.remove('hidden');
    openBtn.setAttribute('aria-expanded', 'true');
  };

  const closeModal = () => {
    modal.classList.add('hidden');
    openBtn.setAttribute('aria-expanded', 'false');
  };

  const applyAndRefresh = async () => {
    renderModal();
    await onFiltersChanged();
  };

  openBtn.addEventListener('click', openModal);
  closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      closeModal();
    }
  });

  velocityAddBtn.addEventListener('click', async () => {
    const raw = velocityInput.value.trim().toUpperCase();
    if (!raw) {
      return;
    }
    useFilterStore.getState().addVelocityBannedTicker(raw);
    velocityInput.value = '';
    await applyAndRefresh();
  });

  velocityInput.addEventListener('keydown', async (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    velocityAddBtn.dispatchEvent(new Event('click'));
  });

  momentumAddBtn.addEventListener('click', async () => {
    const raw = momentumInput.value.trim().toUpperCase();
    if (!raw) {
      return;
    }
    useFilterStore.getState().addMomentumBannedTicker(raw);
    momentumInput.value = '';
    await applyAndRefresh();
  });

  momentumInput.addEventListener('keydown', async (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    momentumAddBtn.dispatchEvent(new Event('click'));
  });

  modal.addEventListener('click', async (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>('.filters-settings-remove-btn');
    if (!target?.dataset.ticker || !target.dataset.scope) {
      return;
    }
    if (target.dataset.scope === 'velocity') {
      useFilterStore.getState().removeVelocityBannedTicker(target.dataset.ticker);
    } else {
      useFilterStore.getState().removeMomentumBannedTicker(target.dataset.ticker);
    }
    await applyAndRefresh();
  });

  velocityResetBtn.addEventListener('click', async () => {
    useFilterStore.getState().resetVelocityBannedTickersToDefaults();
    await applyAndRefresh();
  });

  velocityClearBtn.addEventListener('click', async () => {
    useFilterStore.getState().clearVelocityBannedTickers();
    await applyAndRefresh();
  });

  momentumResetBtn.addEventListener('click', async () => {
    useFilterStore.getState().resetMomentumBannedTickersToDefaults();
    await applyAndRefresh();
  });

  momentumClearBtn.addEventListener('click', async () => {
    useFilterStore.getState().clearMomentumBannedTickers();
    await applyAndRefresh();
  });

  momentumLevelSelect.addEventListener('change', async () => {
    useFilterStore.getState().setMomentumMinLevel(momentumLevelSelect.value as MomentumLevelThreshold);
    await applyAndRefresh();
  });

  momentumTypesContainer.addEventListener('change', async (event) => {
    const input = (event.target as HTMLElement).closest<HTMLInputElement>('input[type="checkbox"][data-type]');
    if (!input?.dataset.type) {
      return;
    }
    useFilterStore
      .getState()
      .toggleMomentumType(input.dataset.type as MomentumAlertType, input.checked);
    await applyAndRefresh();
  });

  momentumTypesResetBtn.addEventListener('click', async () => {
    useFilterStore.getState().resetMomentumTypes();
    await applyAndRefresh();
  });

  const applyHypeSettings = async () => {
    const minPrice = hypeMinPriceInput.value ? parseFloat(hypeMinPriceInput.value) : null;
    const maxPrice = hypeMaxPriceInput.value ? parseFloat(hypeMaxPriceInput.value) : null;
    useFilterStore.getState().setHypePriceRange(minPrice, maxPrice);
    await applyAndRefresh();
  };

  hypeMinPriceInput.addEventListener('input', async () => {
    await applyHypeSettings();
  });

  hypeMaxPriceInput.addEventListener('input', async () => {
    await applyHypeSettings();
  });

  hypePriceClearBtn.addEventListener('click', async () => {
    hypeMinPriceInput.value = '';
    hypeMaxPriceInput.value = '';
    useFilterStore.getState().clearHypePriceRange();
    await applyAndRefresh();
  });

  crossSignalMinHypeInput.addEventListener('input', async () => {
    useFilterStore
      .getState()
      .setCrossSignalMinHypeScore(Number(crossSignalMinHypeInput.value || 40));
    await applyAndRefresh();
  });
}

